// M9.1 — Explicit opt-in dialog for share URLs (SKE-3379). Triggered by
// the Share button on /app/report. Users pick an expiry (1/7/14/30 days)
// and must tick the "I understand" checkbox before we mint a token.
//
// Backdrop click MUST NOT dismiss — the ticket is explicit that the user
// has to make a deliberate Cancel/Create choice. We also never restore
// the checkbox on reopen so a fresh session means a fresh consent.

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { createShareLink, type ReportCompare } from "@/lib/api";
import { pushGtmEvent } from "@/lib/gtm-events";

// M12.2 — the dialog takes raw bounds + compare. The server accepts
// both shapes, so passing bounds straight through is simplest and lines
// up with how ReportOverview now stores its date state.
//
// M12.12 — `compare === "custom"` requires `compareStart` + `compareEnd`.
// The parent passes them through whenever compare is custom; we forward
// them verbatim to createShareLink so the server-side validator doesn't
// 400.
export interface ShareOptInDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  startDate: string;
  endDate: string;
  compare: ReportCompare;
  compareStart?: string;
  compareEnd?: string;
}

type ExpiryChoice = 1 | 7 | 14 | 30;

const EXPIRY_CHOICES: ReadonlyArray<{ value: ExpiryChoice; label: string; help: string }> = [
  { value: 1, label: "1 day", help: "Quick review" },
  { value: 7, label: "7 days", help: "A week for feedback" },
  { value: 14, label: "14 days", help: "Extended review window" },
  // SKE-3433 — was "Recommended — maximum lifetime", which wrapped to
  // 3 lines and crowded the card border. Shortened so all four cards
  // wrap identically; the max-lifetime rationale moved to the
  // help text below the grid.
  { value: 30, label: "30 days", help: "Recommended" },
];

export function ShareOptInDialog({
  open,
  onOpenChange,
  propertyId,
  startDate,
  endDate,
  compare,
  compareStart,
  compareEnd,
}: ShareOptInDialogProps) {
  const [expiryDays, setExpiryDays] = useState<ExpiryChoice>(30);
  const [confirmed, setConfirmed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Reset every time the dialog opens — the ticket explicitly says the
  // confirmation checkbox must never auto-carry across invocations, and
  // it's simpler (and less error-prone) to reset all transient state too.
  useEffect(() => {
    if (open) {
      setExpiryDays(30);
      setConfirmed(false);
      setError(null);
      setCreatedUrl(null);
      setCopied(false);
    }
  }, [open]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const result = await createShareLink({
        propertyId,
        startDate,
        endDate,
        compare,
        ...(compare === "custom" && compareStart && compareEnd ? { compareStart, compareEnd } : {}),
        expiresInDays: expiryDays,
      });
      setCreatedUrl(result.url);
      pushGtmEvent("share_link_created", {
        property_id: propertyId,
        expires_in_days: expiryDays,
        compare,
      });
    } catch (e) {
      setError((e as Error).message || "Something went wrong creating your share link.");
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!createdUrl) return;
    try {
      await navigator.clipboard.writeText(createdUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Some browsers disallow clipboard writes without user gesture
      // tracking; surface nothing — the input is selectable.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // M12.12 — widened from sm:max-w-lg (32rem) to sm:max-w-2xl
        // (42rem) so the 4-card expiry row fits without wrapping, and
        // dropped the top-right close X per design — the Cancel button
        // in the footer is the single dismissal affordance.
        className="sm:max-w-2xl"
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Create a share link</DialogTitle>
          <DialogDescription>
            Anyone with the link can view this report without signing in — choose carefully.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2 pb-1">
          {createdUrl ? (
            <CreatedLinkPanel url={createdUrl} copied={copied} onCopy={handleCopy} />
          ) : (
            <>
              <section aria-labelledby="share-whats-shared" className="space-y-2">
                <h3
                  id="share-whats-shared"
                  className="text-[11px] font-bold uppercase tracking-wide text-purple-700"
                >
                  What's shared
                </h3>
                <ul
                  role="list"
                  className="space-y-1.5 text-sm text-neutral-700 [&_i]:mr-2 [&_i]:text-purple-600"
                >
                  <li>
                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                    Traffic KPIs, platforms, landing pages, geography, recommendations, UTM health
                  </li>
                </ul>
              </section>

              <section aria-labelledby="share-whats-not" className="space-y-2">
                <h3
                  id="share-whats-not"
                  className="text-[11px] font-bold uppercase tracking-wide text-neutral-500"
                >
                  Not shared
                </h3>
                <ul
                  role="list"
                  className="space-y-1.5 text-sm text-neutral-600 [&_i]:mr-2 [&_i]:text-neutral-400"
                >
                  <li>
                    <i className="fa-solid fa-xmark text-xs" aria-hidden="true" />
                    Your GA4 grant, email, exports, settings, or any account access
                  </li>
                  <li>
                    <i className="fa-solid fa-xmark text-xs" aria-hidden="true" />
                    Download PDF / Download XLSX — disabled for share-link viewers
                  </li>
                </ul>
              </section>

              <fieldset className="space-y-2">
                <legend className="text-sm font-semibold text-neutral-900">Link expires in</legend>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {EXPIRY_CHOICES.map((choice) => {
                    const id = `share-expiry-${choice.value}`;
                    const selected = expiryDays === choice.value;
                    return (
                      <label
                        key={choice.value}
                        htmlFor={id}
                        className={cn(
                          "flex cursor-pointer flex-col gap-0.5 rounded-lg border px-3 py-2.5 text-left transition-colors",
                          selected
                            ? "border-purple-500 bg-purple-50 ring-1 ring-purple-500"
                            : "border-neutral-200 bg-white hover:border-neutral-300",
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <span className="group inline-grid size-4 grid-cols-1">
                            <input
                              id={id}
                              name="share-expiry"
                              type="radio"
                              value={choice.value}
                              checked={selected}
                              onChange={() => setExpiryDays(choice.value)}
                              className="checked:border-purple-600 checked:bg-purple-600 focus-visible:outline-purple-600 col-start-1 row-start-1 appearance-none rounded-full border border-gray-300 bg-white focus-visible:outline-2 focus-visible:outline-offset-2 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                            />
                            <span className="pointer-events-none col-start-1 row-start-1 size-[round(down,40%,1px)] self-center justify-self-center rounded-full bg-white group-not-has-checked:opacity-0" />
                          </span>
                          <span className="text-sm font-semibold text-neutral-900">
                            {choice.label}
                          </span>
                        </span>
                        <span className="pl-6 text-xs text-neutral-500">{choice.help}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-neutral-500">
                  30 days is the maximum link lifetime — pick a shorter window if the audience
                  shouldn't keep access that long.
                </p>
              </fieldset>

              <div className="rounded-lg bg-amber-50 px-3 py-3 ring-1 ring-amber-600/20">
                <label
                  htmlFor="share-confirm"
                  className="flex cursor-pointer items-start gap-3 text-sm text-neutral-800"
                >
                  <span className="h-lh items-center flex shrink-0">
                    <span className="group inline-grid size-4 grid-cols-1">
                      <input
                        id="share-confirm"
                        name="share-confirm"
                        type="checkbox"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                        className="checked:border-purple-600 checked:bg-purple-600 focus-visible:outline-purple-600 col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white focus-visible:outline-2 focus-visible:outline-offset-2 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                      />
                      <svg
                        viewBox="0 0 14 14"
                        fill="none"
                        className="pointer-events-none col-start-1 row-start-1 size-7/8 self-center justify-self-center stroke-white"
                      >
                        <path
                          d="M3 8L6 11L11 3.5"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="group-not-has-checked:opacity-0"
                        />
                      </svg>
                    </span>
                  </span>
                  <span>
                    I understand anyone with this link can view this report without signing in.
                  </span>
                </label>
              </div>

              {error ? (
                <p role="alert" className="text-sm text-red-700">
                  {error}
                </p>
              ) : null}
            </>
          )}
        </div>

        <DialogFooter>
          {createdUrl ? (
            <Button type="button" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleCreate} disabled={!confirmed || creating}>
                {creating ? "Creating…" : "Create shareable link"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreatedLinkPanel({
  url,
  copied,
  onCopy,
}: {
  url: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900 ring-1 ring-emerald-600/20">
        <p className="flex items-center gap-2 font-semibold">
          <i className="fa-solid fa-circle-check text-emerald-600" aria-hidden="true" />
          Share link created
        </p>
        <p className="mt-0.5 text-xs text-emerald-800/80">
          Copy it now — the link is the only record you'll see. You can revoke later from Exports.
        </p>
      </div>
      <div className="flex gap-2">
        <label htmlFor="share-url" className="sr-only">
          Share URL
        </label>
        <input
          id="share-url"
          name="share-url"
          readOnly
          value={url}
          className="max-sm:text-base/6 flex-1 rounded-md bg-white px-3 py-2 text-sm ring-1 ring-neutral-950/10 focus:outline-2 -outline-offset-1 focus:outline-purple-500"
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button type="button" variant="ghost" onClick={onCopy} aria-live="polite">
          <i
            className={cn(
              "fa-regular",
              copied ? "fa-check text-emerald-600" : "fa-copy",
              "text-sm",
            )}
            aria-hidden="true"
          />
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
    </div>
  );
}
